/**
 * Substituição da fonte Material Symbols por SVG-via-CSS no HTML exportado.
 *
 * Motivação: a fonte woff2 completa custava ~1 MB; subset (fontTools) ainda
 * ~247 KB por causa de glifos complexos. Análise real (ago/2026) mostrou que
 * SÓ DOIS ícones aparecem no HTML exportado: `mail` e `language`. Trocar por
 * mask-image data-URI zera o custo do @font-face e cada instância no HTML
 * ocupa ~40 chars (`<span class="ms-icon ms-icon-mail"></span>`).
 *
 * Cor: `background-color: currentColor` — o ícone acompanha a cor do texto
 * do contexto (light/dark, chair/autor, etc.), mesmo comportamento da fonte.
 *
 * Se algum ícone NOVO passar a ser renderizado no export, adicionar path
 * aqui + regenerar. Sintoma: o `<mat-icon>` sobrevive no HTML final e aparece
 * como TEXTO CRU (ex.: "info") por falta de fonte.
 */

/** Paths oficiais Material Symbols Outlined, viewBox `0 -960 960 960`. */
export const MS_ICON_PATHS: { [name: string]: string } = {
  mail: 'M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440ZM480-520l320-200H160l320 200ZM160-640v-80 480-400Z',
  language: 'M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q83 0 155.5 31.5t127 86q54.5 54.5 86 127T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Zm0-82q26-36 45-75t31-83H404q12 44 31 83t45 75Zm-104-16q-18-33-31.5-68.5T322-320H204q29 50 72.5 87t99.5 55Zm208 0q56-18 99.5-55t72.5-87H638q-9 38-22.5 73.5T584-178ZM170-400h136q-3-20-4.5-39.5T300-480q0-21 1.5-40.5T306-560H170q-5 20-7.5 39.5T160-480q0 21 2.5 40.5T170-400Zm216 0h188q3-20 4.5-39.5T580-480q0-21-1.5-40.5T574-560H386q-3 20-4.5 39.5T380-480q0 21 1.5 40.5T386-400Zm268 0h136q5-20 7.5-39.5T800-480q0-21-2.5-40.5T790-560H654q3 20 4.5 39.5T660-480q0 21-1.5 40.5T654-400Zm-16-240h118q-29-50-72.5-87T584-782q18 33 31.5 68.5T638-640Zm-234 0h152q-12-44-31-83t-45-75q-26 36-45 75t-31 83Zm-200 0h118q9-38 22.5-73.5T376-782q-56 18-99.5 55T204-640Z',
};

/** CSS gerado uma vez. Injetar em `<style>` (single/PDF) ou `assets/styles.css` (zip). */
export const MS_ICON_CSS: string = (() => {
  const base = `.ms-icon{display:inline-block;width:1em;height:1em;vertical-align:middle;background-color:currentColor;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center;mask-position:center;-webkit-mask-size:contain;mask-size:contain;}`;
  const rules = Object.entries(MS_ICON_PATHS).map(([name, d]) => {
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 -960 960 960'><path d='${d}'/></svg>`;
    const uri = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    return `.ms-icon-${name}{-webkit-mask-image:url("${uri}");mask-image:url("${uri}");}`;
  }).join('');
  return base + rules;
})();

/** Substitui `<mat-icon material-symbols-outlined>NAME</mat-icon>` (e variantes
 *  com `<span>`) por `<span class="ms-icon ms-icon-{NAME}"></span>`. Ícone
 *  desconhecido: mantém o elemento original (fallback). */
export function replaceMatIconsWithSvg(html: string): string {
  const stub = (name: string): string | null =>
    MS_ICON_PATHS[name] ? `<span class="ms-icon ms-icon-${name}" aria-hidden="true"></span>` : null;
  return html
    .replace(/<mat-icon\b[^>]*material-symbols-outlined[^>]*>\s*([a-z_]+)\s*<\/mat-icon>/g,
      (m, name) => stub(name) || m)
    .replace(/<span\b[^>]*material-symbols-outlined[^>]*>\s*([a-z_]+)\s*<\/span>/g,
      (m, name) => stub(name) || m);
}

/** Remove overhead de Angular dev-mode do HTML exportado — atributos e
 *  comentários que só existem em `ng serve` (prod build já não os emite):
 *  - `ng-reflect-*` (debug do binding) — ~28% do peso
 *  - `_ngcontent-*` (view encapsulation) — ~18%; o CSS pré-baked aplica por
 *    classe pura, sem encapsulation, então esses atributos não fazem nada
 *  - `<!--bindings={...}-->` (comentário-âncora do Angular)
 *  - `_nghost-*` (host do componente) — mesma razão
 *  - `ng-star-inserted` (marca ligada ao *ngIf)
 *  Redução típica: ~63% do tamanho de cada HTML de submissão. */
export function stripAngularDevAttrs(html: string): string {
  return html
    .replace(/<!--bindings=\{[^}]*\}-->/g, '')
    .replace(/\s+ng-reflect-[a-z0-9-]+="[^"]*"/g, '')
    .replace(/\s+_ngcontent-[a-z0-9-]+=""/g, '')
    .replace(/\s+_nghost-[a-z0-9-]+=""/g, '')
    .replace(/\s+ng-star-inserted\b/g, '');   // dentro de class="..." vira ""
}
